import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { BankInfo } from '../../types';

interface ProfileData {
    displayName: string;
    email?: string;
    createdAt?: number;
    lastAccess?: number;
    bankInfo?: BankInfo;
    isAnonymous: boolean;
    isOwner: boolean;
    userId: string;
}

interface BankInfoSectionProps {
    displayData: ProfileData;
    canEdit: boolean;
    onEditBankInfo: () => void;
    isOwnerEdit?: boolean;
}

const BankInfoSection: React.FC<BankInfoSectionProps> = ({
                                                             displayData,
                                                             canEdit,
                                                             onEditBankInfo,
                                                             isOwnerEdit = false
                                                         }) => {
    const { t } = useTranslation();

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                {t('profile.components.bankInfoSection.title')}
                {isOwnerEdit && (
                    <span className="ml-2 text-sm text-blue-600 dark:text-blue-400">
                        {t('profile.components.bankInfoSection.ownerEdit')}
                    </span>
                )}
            </h2>

            {displayData.bankInfo ? (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                {t('profile.components.bankInfoSection.bankName')}
                            </label>
                            <p className="text-gray-900 dark:text-white">
                                {displayData.bankInfo.bankName}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                {t('profile.components.bankInfoSection.accountHolder')}
                            </label>
                            <p className="text-gray-900 dark:text-white">
                                {displayData.bankInfo.accountHolder}
                            </p>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                {t('profile.components.bankInfoSection.accountNumber')}
                            </label>
                            <p className="text-gray-900 dark:text-white font-mono">
                                {displayData.bankInfo.accountNumber}
                            </p>
                        </div>
                    </div>

                    {canEdit && (
                        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={onEditBankInfo}
                            className="glass-btn-primary inline-flex items-center !px-4 !py-2 text-sm"
                            >
                                <FontAwesomeIcon icon={faEdit} className="mr-2" />
                                {t('profile.components.bankInfoSection.editBankInfo')}
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-8">
                    <FontAwesomeIcon
                        icon={faCreditCard}
                        className="text-4xl text-gray-300 dark:text-gray-600 mb-4"
                    />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                        {t('profile.components.bankInfoSection.noBankInfo')}
                    </p>
                    {canEdit && (
                        <button
                            onClick={onEditBankInfo}
                            className="glass-btn-primary inline-flex items-center !px-4 !py-2 text-sm"
                        >
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            {t('profile.components.bankInfoSection.addBankInfo')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default BankInfoSection;